import {
  ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Injector, afterNextRender, computed, effect, inject, input, signal, viewChild
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { animate, query, style, transition, trigger } from '@angular/animations';
import { LembreteNotificacaoService } from '../../../service/lembrete-notificacao.service';
import { AberturaContatoService } from '../../../service/abertura-contato.service';
import { LembreteNotificacao } from '../../../model/lembrete/lembrete-notificacao.model';
import { EtapaAvisoLembreteEnum } from '../../../enum/etapa-aviso-lembrete.enum';
import { MethodsUtils } from '../../../util/methods.utils';
import { CURVA_ANIMACAO_LEMBRETE, DURACAO_RETORNO_CARD_LEMBRETE_MS } from '../../../util/lembrete-notificacao.constantes';
import { LembreteNotificacaoModalComponent } from '../lembrete-notificacao-modal/lembrete-notificacao-modal.component';
import {
  DURACAO_ENTRADA_BANNER_LEMBRETE_MS, DURACAO_ENTRADA_CARD_LEMBRETE_MS, DURACAO_SAIDA_BANNER_LEMBRETE_MS, DURACAO_SAIDA_CARD_LEMBRETE_MS,
  INTERVALO_RELOGIO_NOTIFICACAO_MS, VARIAVEL_ALTURA_BANNER_LEMBRETE
} from './utils/lembrete-notificacao.constantes';

@Component({
  selector: 'app-lembrete-notificacao',
  standalone: true,
  imports: [DatePipe, LembreteNotificacaoModalComponent],
  templateUrl: './lembrete-notificacao.component.html',
  styleUrl: './lembrete-notificacao.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('entradaCard', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(120%) scale(.9)' }),
        animate(`${DURACAO_ENTRADA_CARD_LEMBRETE_MS}ms ${CURVA_ANIMACAO_LEMBRETE}`, style({ opacity: 1, transform: 'translateX(0) scale(1)' }))
      ]),
      transition(':leave', [
        animate(`${DURACAO_SAIDA_CARD_LEMBRETE_MS}ms ease-in`, style({ opacity: 0, transform: 'translateX(120%) scale(.9)' }))
      ])
    ]),
    trigger('entradaBanner', [
      transition(':enter', [
        query('.lnot-banner', style({ transform: 'translateY(-100%)', opacity: 0 })),
        query('.lnot-banner', animate(`${DURACAO_ENTRADA_BANNER_LEMBRETE_MS}ms ${CURVA_ANIMACAO_LEMBRETE}`, style({ transform: 'translateY(0)', opacity: 1 })))
      ]),
      transition(':leave', [
        style({ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5 }),
        query('.lnot-banner', animate(`${DURACAO_SAIDA_BANNER_LEMBRETE_MS}ms ease-in`, style({ transform: 'translateY(-100%)', opacity: 0 })))
      ])
    ])
  ]
})
export class LembreteNotificacaoComponent {
  readonly mobile = input(false);

  private readonly notificacaoService = inject(LembreteNotificacaoService);
  private readonly aberturaContatoService = inject(AberturaContatoService);
  private readonly injector = inject(Injector);
  private readonly card = viewChild<ElementRef<HTMLElement>>('card');
  private readonly areaBanner = viewChild<ElementRef<HTMLElement>>('areaBanner');
  private readonly observadorBanner = new ResizeObserver(entradas => this.definirAlturaBanner(entradas[0]?.borderBoxSize[0]?.blockSize ?? 0));

  readonly notificacoes = this.notificacaoService.notificacoes;
  readonly total = this.notificacaoService.total;
  readonly topo = computed(() => this.notificacoes().slice(0, 1));
  readonly agora = signal(new Date());
  readonly idModal = signal<number | null>(null);
  readonly notificacaoModal = computed(() => this.notificacoes().find(n => n.id === this.idModal()) ?? null);
  origemModal: DOMRect | null = null;

  constructor() {
    interval(INTERVALO_RELOGIO_NOTIFICACAO_MS)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.agora.set(new Date()));

    effect(() => {
      if (this.idModal() !== null && !this.notificacaoModal()) this.idModal.set(null);
    }, { allowSignalWrites: true });

    effect(() => {
      const area = this.areaBanner()?.nativeElement;
      this.observadorBanner.disconnect();
      if (area) {
        this.observadorBanner.observe(area);
        return;
      }
      this.definirAlturaBanner(0);
    });

    inject(DestroyRef).onDestroy(() => {
      this.observadorBanner.disconnect();
      this.definirAlturaBanner(0);
    });
  }

  podeAgir(notificacao: LembreteNotificacao): boolean {
    return this.notificacaoService.podeAgir(notificacao);
  }

  quando(notificacao: LembreteNotificacao): string {
    if (notificacao.etapaAviso === EtapaAvisoLembreteEnum.EXECUCAO) return 'agora';
    return MethodsUtils.rotuloAntecedencia(new Date(notificacao.dataHora), this.agora());
  }

  fotoContato(notificacao: LembreteNotificacao): string {
    return MethodsUtils.fotoPerfilContato(notificacao.contatoFotoPerfilUrl, notificacao.contatoStatus);
  }

  marcarFotoIndisponivel(notificacao: LembreteNotificacao): void {
    MethodsUtils.marcarFotoPerfilIndisponivel(notificacao.contatoFotoPerfilUrl);
  }

  abrir(notificacao: LembreteNotificacao, event?: Event): void {
    event?.stopPropagation();
    this.origemModal = this.card()?.nativeElement.getBoundingClientRect() ?? null;
    this.idModal.set(notificacao.id);
  }

  fecharModal(retorno?: DOMRect | null): void {
    this.idModal.set(null);
    if (retorno) afterNextRender(() => this.animarRetornoCard(retorno), { injector: this.injector });
  }

  abrirContato(notificacao: LembreteNotificacao, retorno: DOMRect | null): void {
    this.fecharModal(retorno);
    if (notificacao.contatoNumero) this.aberturaContatoService.abrir(notificacao.contatoNumero);
  }

  adiar(notificacao: LembreteNotificacao, minutos?: number, event?: Event): void {
    event?.stopPropagation();
    this.fecharModal();
    this.notificacaoService.adiar(notificacao.id, minutos);
  }

  concluir(notificacao: LembreteNotificacao, event?: Event): void {
    event?.stopPropagation();
    this.fecharModal();
    this.notificacaoService.concluir(notificacao.id);
  }

  dispensar(notificacao: LembreteNotificacao, event?: Event): void {
    event?.stopPropagation();
    this.fecharModal();
    this.notificacaoService.dispensar(notificacao.id);
  }

  private animarRetornoCard(retorno: DOMRect): void {
    const card = this.card()?.nativeElement;
    if (!card) return;
    card.animate(
      [
        { transform: MethodsUtils.transformacaoFlip(retorno, card.getBoundingClientRect()), opacity: 0.85 },
        { transform: 'translate(0, 0) scale(1)', opacity: 1 }
      ],
      { duration: MethodsUtils.duracaoAnimacao(DURACAO_RETORNO_CARD_LEMBRETE_MS), easing: CURVA_ANIMACAO_LEMBRETE }
    );
  }

  private definirAlturaBanner(altura: number): void {
    document.documentElement.style.setProperty(VARIAVEL_ALTURA_BANNER_LEMBRETE, `${Math.ceil(altura)}px`);
  }
}
